import styled from "styled-components";
import { theme } from "./theme";

/**
 * Public customer pages opened from the temporary-access emails. They sit
 * outside the portal layout, so they carry their own Davey header and footer.
 */

export type StatusTone = "success" | "declined" | "info" | "error" | "prompt";
type ActionVariant = "primary" | "secondary" | "danger";

const toneColors: Record<StatusTone, { background: string; color: string }> = {
  success: { background: theme.colors.primary, color: theme.colors.onPrimary },
  prompt: { background: theme.colors.primary, color: theme.colors.onPrimary },
  declined: { background: "#4b4b4b", color: theme.colors.white },
  info: { background: theme.colors.lightGrey, color: theme.colors.textPrimary },
  error: { background: theme.colors.errorBg, color: theme.colors.error },
};

const actionColors: Record<
  ActionVariant,
  { background: string; color: string; border: string; hover: string }
> = {
  primary: {
    background: theme.colors.primary,
    color: theme.colors.onPrimary,
    border: theme.colors.primary,
    hover: theme.colors.primaryLight,
  },
  secondary: {
    background: "transparent",
    color: theme.colors.textPrimary,
    border: theme.colors.border,
    hover: theme.colors.bgLight,
  },
  danger: {
    background: "#c62828",
    color: theme.colors.white,
    border: "#c62828",
    hover: "#a61f1f",
  },
};

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${theme.colors.grey};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.fonts.primary};
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  background-color: ${theme.colors.secondary};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1rem 1.25rem;
  }
`;

export const HeaderLogo = styled.img`
  display: block;
  height: 30px;
  width: auto;
`;

export const PageMain = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

export const Card = styled.section`
  width: min(36rem, 100%);
  padding: 2.5rem 2rem;
  border-radius: ${theme.borderRadius.medium};
  background-color: ${theme.colors.surface};
  box-shadow: ${theme.shadows.medium};
  text-align: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 2rem 1.25rem;
  }
`;

export const StatusIcon = styled.div<{ $tone: StatusTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  background-color: ${({ $tone }) => toneColors[$tone].background};
  color: ${({ $tone }) => toneColors[$tone].color};

  .pi {
    font-size: 1.75rem;
  }
`;

export const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: ${theme.fontSizes.large};
  font-weight: ${theme.fontWeights.bold};
  line-height: 1.3;
  color: ${theme.colors.textPrimary};
`;

export const Message = styled.p`
  max-width: 30rem;
  margin: 0 auto;
  font-size: ${theme.fontSizes.regular};
  line-height: 1.6;
  color: ${theme.colors.textPrimary};
`;

export const Hint = styled.p`
  max-width: 30rem;
  margin: 1.25rem auto 0;
  font-size: ${theme.fontSizes.small};
  line-height: 1.55;
  color: ${theme.colors.textSecondary};
`;

export const Details = styled.dl`
  max-width: 24rem;
  margin: 1.5rem auto 0;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.small};
  background-color: ${theme.colors.bgLight};
  text-align: left;
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.4rem 0;
  font-size: ${theme.fontSizes.small};

  & + & {
    border-top: 1px solid ${theme.colors.borderLight};
  }

  dt {
    color: ${theme.colors.textSecondary};
  }

  dd {
    margin: 0;
    font-weight: ${theme.fontWeights.bold};
    text-align: right;
    overflow-wrap: anywhere;
  }
`;

export const Confirmation = styled.div`
  max-width: 28rem;
  margin: 1.5rem auto 0;
  padding: 1rem;
  border: 1px solid ${theme.colors.primaryDark};
  border-radius: ${theme.borderRadius.small};
  background-color: ${theme.colors.warningBg};

  /* Programmatic focus target only; the buttons inside keep their rings. */
  &:focus {
    outline: none;
  }

  p {
    margin: 0;
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.warningText};
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

export const ActionButton = styled.button<{ $variant: ActionVariant }>`
  min-height: 2.75rem;
  padding: 0.65rem 1.25rem;
  border: 1px solid ${({ $variant }) => actionColors[$variant].border};
  border-radius: ${theme.borderRadius.small};
  background-color: ${({ $variant }) => actionColors[$variant].background};
  color: ${({ $variant }) => actionColors[$variant].color};
  font-family: inherit;
  font-size: ${theme.fontSizes.regular};
  font-weight: ${theme.fontWeights.bold};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ $variant }) => actionColors[$variant].hover};
  }

  &:focus-visible {
    outline: 3px solid ${theme.colors.tertiary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ProcessingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 0;
  color: ${theme.colors.textSecondary};

  .pi {
    font-size: 2rem;
    color: ${theme.colors.tertiary};
  }

  p {
    margin: 0;
  }
`;

export const PageFooter = styled.footer`
  padding: 1rem 2rem;
  background-color: ${theme.colors.secondary};
  color: #9d9d9d;
  font-size: ${theme.fontSizes.small};
  text-align: center;
`;
